import React, { createContext, useContext, useEffect, useState } from "react";
import { clearUserFromStorage } from "../utils/userMapper";
import { clearRybbit } from "../utils/rybbit";

interface UserInfo {
  user_id?: string;
  email?: string;
  name?: string;
  org_id?: string;
  roles?: string[];
  permissions?: string[];
  [key: string]: any;
}

interface UserContextType {
  user: UserInfo | null;
  setUser: (user: UserInfo | null) => void;
  logout: () => void;
  refreshUser: () => void;
  isLoading: boolean;
}

const SESSION_DURATION = 24 * 60 * 60 * 1000;

const UserContext = createContext<UserContextType>({
  user: null,
  setUser: () => {},
  logout: () => {},
  refreshUser: () => {},
  isLoading: true,
});

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user_info");
      if (storedUser) {
        setUser(JSON.parse(storedUser));
        if (!localStorage.getItem("login_time")) {
          localStorage.setItem("login_time", Date.now().toString());
        }
      }
    } catch (err) {
      console.error("❌ Failed to parse user from localStorage:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /* -----------------------------
   🔹 Auto logout after 24 hours
  ------------------------------*/
  useEffect(() => {
    const interval = setInterval(() => {
      const loginTime = localStorage.getItem("login_time");

      if (!loginTime) return;

      const elapsed = Date.now() - Number(loginTime);

      if (elapsed >= SESSION_DURATION) {
        console.log("⏰ Session expired (24h)");

        logout();
      }
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const logout = () => {
    console.log("🚪 Logging out");
    clearUserFromStorage(); // This clears both user and Chatwoot data
    clearRybbit();
    setUser(null);
    window.location.href = "/login"; 
  };

  const refreshUser = () => {
    try {
      const storedUser = localStorage.getItem("user_info");
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } catch (err) {
      console.error("❌ Failed to refresh user from localStorage:", err);
    }
  };

  return (
    <UserContext.Provider
      value={{ user, setUser, logout, refreshUser, isLoading }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
